import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import PageSEO from "@/components/seo/PageSEO";
import ConsumerFooter from "@/components/consumer/landing/ConsumerFooter";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

const ConsumerForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [rateLimitRemaining, setRateLimitRemaining] = useState<number | null>(null);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!email.trim()) {
      setError("Please enter your email address");
      setIsLoading(false);
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      setIsLoading(false);
      return;
    }

    try {
      const { error: magicLinkError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/consumer/dashboard?reset=true`,
        },
      });

      if (magicLinkError) {
        if (magicLinkError.message.includes("rate limit")) {
          setError("Too many reset attempts. Please wait a few minutes before trying again.");
          setRateLimitRemaining(300);
        } else if (magicLinkError.message.includes("Invalid email")) {
          setError("Please enter a valid email address");
        } else if (magicLinkError.message.includes("User not found")) {
          setIsSubmitted(true);
        } else if (magicLinkError.message.includes("Email not confirmed")) {
          setError("Please verify your email address first. Check your inbox for a verification email.");
        } else {
          setError("Unable to send reset email. Please check your email address and try again.");
        }

        if (!magicLinkError.message.includes("User not found")) {
          return;
        }
      }

      setIsSubmitted(true);

      toast({
        title: "Reset link sent",
        description: "Please check your email for a login link to reset your password.",
      });

      if (rateLimitRemaining) {
        const countdown = setInterval(() => {
          setRateLimitRemaining((prev) => {
            if (prev && prev <= 1) {
              clearInterval(countdown);
              return null;
            }
            return prev ? prev - 1 : null;
          });
        }, 1000);
      }
    } catch (err: any) {
      if (!navigator.onLine) {
        setError("No internet connection. Please check your network and try again.");
      } else {
        setError("An unexpected error occurred. Please try again later.");
      }

      toast({
        title: "Error",
        description: err.message || "Failed to send reset link. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080c14] text-white flex flex-col">
      <PageSEO
        title="Forgot Password | GoDyspo Homeowner"
        description="Reset your GoDyspo homeowner account password."
        canonical="/consumer/forgot-password"
      />

      <header className="border-b border-border/30 bg-[#080c14]/95 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto flex items-center justify-between h-16 px-4">
          <Link to="/consumer/login" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back to Sign In</span>
          </Link>
          <Link to="/consumer" className="text-xl font-bold text-[#ffd700]">GoDyspo</Link>
          <div className="w-24" />
        </div>
      </header>

      <div className="flex-1 container mx-auto max-w-md px-4 pt-16 pb-12">
        <h1 className="text-3xl font-bold mb-2 text-center text-[#ffd700]">Forgot Password</h1>
        <p className="text-slate-400 text-center mb-8">
          Enter your email and we'll send you a login link to reset your password
        </p>

        {isSubmitted ? (
          <div className="space-y-4 text-center">
            <div className="bg-green-500/20 text-green-400 p-4 rounded-md flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
              <span>Password reset link has been sent to your email.</span>
            </div>
            <div className="space-y-2 text-sm text-slate-400">
              <p>Please check your email inbox and click the login link to automatically sign in and reset your password.</p>
              <Alert className="border-slate-700 bg-slate-800">
                <AlertCircle className="h-4 w-4 text-[#ffd700]" />
                <AlertDescription className="text-slate-300">
                  <strong className="text-[#ffd700]">Don't see the email?</strong>
                  <ul className="mt-1 list-disc list-inside space-y-1">
                    <li>Check your spam or junk folder</li>
                    <li>Make sure you entered the correct email address</li>
                    <li>Wait a few minutes - emails can sometimes be delayed</li>
                  </ul>
                </AlertDescription>
              </Alert>
            </div>
            <Link
              to="/consumer/login"
              className="inline-block text-sm text-[#ffd700] hover:text-[#e6c200] underline-offset-4 hover:underline mt-4"
            >
              Back to Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert className="border-red-500/50 bg-red-500/10">
                <AlertCircle className="h-4 w-4 text-red-400" />
                <AlertDescription className="text-red-400">{error}</AlertDescription>
              </Alert>
            )}

            {rateLimitRemaining && (
              <Alert className="border-[#ffd700]/50 bg-[#ffd700]/10">
                <Clock className="h-4 w-4 text-[#ffd700]" />
                <AlertDescription className="text-[#ffd700]">
                  Please wait {formatTime(rateLimitRemaining)} before requesting another reset email.
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                required
                className="bg-white/5 border-white/10 text-white"
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                disabled={isLoading || !!rateLimitRemaining}
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#ffd700] text-[#080c14] hover:bg-[#e6c200] font-semibold"
              size="lg"
              disabled={isLoading || !!rateLimitRemaining || !email.trim()}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#080c14]" />
                  Sending Login Link...
                </div>
              ) : (
                "Send Login Link"
              )}
            </Button>

            <p className="text-center text-sm text-slate-400">
              The login link will automatically sign you in and allow you to change your password.
            </p>
          </form>
        )}
      </div>

      <ConsumerFooter />
    </div>
  );
};

export default ConsumerForgotPasswordPage;
